import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import {
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Toolbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import i18next from 'i18next';
import moment from 'moment';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import FileUploadButton from 'components/FileUploadButton';
import { Helmet } from 'react-helmet';
import { getFullBackendUrl } from 'helper/download';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
// Avoid formatDocumentTitle here because it uses hooks internally
import {
  VIEW_CONTRACT_QUERY,
  UPDATE_CONTRACT_MUTATION,
  CREATE_CONTRACT_MUTATION,
  DELETE_CONTRACT_MUTATION,
  CREATE_MANUAL_INVOICE_MUTATION,
  UPLOAD_CONTRACT_DOCUMENT_MUTATION,
  DELETE_CONTRACT_DOCUMENT_MUTATION,
} from './gql';
import { CONTRACT_DOCUMENT_URL_QUERY, LIST_HOTEL_CONTRACTS_QUERY } from './gql';
import { contractTypeLabels } from 'helper/contracts';

const invoiceIntervalLabels = {
  ONCE: 'contracts-interval-ONCE',
  M1: 'contracts-interval-M1',
  M3: 'contracts-interval-M3',
  M6: 'contracts-interval-M6',
  M12: 'contracts-interval-M12',
};

const sapStatusLabels = {
  PENDING: 'contracts-sap-PENDING',
  SENT: 'contracts-sap-SENT',
  FAILED: 'contracts-sap-FAILED',
};

interface ContractDetailProps {
  isEdit?: boolean;
  isCreate?: boolean;
}

const ContractDetail: React.FC<ContractDetailProps> = ({ isEdit = false, isCreate = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const search = new URLSearchParams(location.search);
  const hotelIdFromQuery = search.get('hotelId');
  const [manualInvoiceDialogOpen, setManualInvoiceDialogOpen] = useState(false);
  const [manualInvoiceDate, setManualInvoiceDate] = useState('');
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const dispatch = useDispatch();
  const { data, loading, refetch } = useQuery(VIEW_CONTRACT_QUERY, {
    variables: { id: id ? parseInt(id!) : 0 },
    skip: !id || isCreate,
  });

  const formMethods = useForm({
    defaultValues: {
      type: '',
      validFrom: '',
      validTo: '',
      invoiceInterval: '',
      amountCents: 0,
      description: '',
    },
  });

  const { register, handleSubmit, reset, watch, control } = formMethods;

  useEffect(() => {
    if (isCreate) {
      reset({
        type: 'SYMPOSION_HOTEL_CONTRACT',
        validFrom: moment().format('YYYY-MM-DD'),
        validTo: moment().add(1, 'year').format('YYYY-MM-DD'),
        invoiceInterval: 'ONCE',
        amountCents: 0,
        description: '',
      });
    } else if (data?.contract) {
      reset({
        type: data.contract.type,
        validFrom: moment(data.contract.validFrom).format('YYYY-MM-DD'),
        validTo: moment(data.contract.validTo).format('YYYY-MM-DD'),
        invoiceInterval: data.contract.invoiceInterval,
        amountCents: data.contract.amountCents / 100,
        description: data.contract.description || '',
      });
    }
  }, [data, reset, isCreate]);

  const [updateContract] = useMutation(UPDATE_CONTRACT_MUTATION, {
    onCompleted: () => {
      dispatchMessage(dispatch, String(i18next.t('contracts-update-success')));
      refetch();
    },
    onError: (error) => {
      dispatchException(dispatch, error);
    },
  });

  const [createContract] = useMutation(CREATE_CONTRACT_MUTATION, {
    onCompleted: (data) => {
      dispatchMessage(dispatch, String(i18next.t('contracts-create-success')));
      const newId = data?.createContract?.id;
      const hotelId = hotelIdFromQuery ? parseInt(hotelIdFromQuery) : null;
      if (newId && hotelId) {
        navigate(`/contracts/${newId}/edit`, { replace: true });
      } else if (hotelId) {
        navigate(`/settings/hotels/${hotelId}/contracts`);
      } else {
        navigate(-1);
      }
    },
    onError: (error) => {
      dispatchException(dispatch, error);
    },
    refetchQueries: () => {
      const hotelId = hotelIdFromQuery ? parseInt(hotelIdFromQuery) : NaN;
      return hotelId && !Number.isNaN(hotelId)
        ? [
          {
            query: LIST_HOTEL_CONTRACTS_QUERY,
            variables: { hotelId },
          },
        ]
        : [];
    },
    awaitRefetchQueries: true,
  });

  const [deleteContract] = useMutation(DELETE_CONTRACT_MUTATION, {
    onCompleted: () => {
      dispatchMessage(dispatch, String(i18next.t('contracts-delete-success')));
    },
    onError: (error) => {
      dispatchException(dispatch, error);
    },
  });

  const [createManualInvoice] = useMutation(CREATE_MANUAL_INVOICE_MUTATION, {
    onCompleted: () => {
      dispatchMessage(dispatch, String(i18next.t('contracts-invoice-create-success')));
      setManualInvoiceDialogOpen(false);
      setManualInvoiceDate('');
      refetch();
    },
    onError: (error) => {
      dispatchException(dispatch, error);
    },
  });

  const [uploadDocument] = useMutation(UPLOAD_CONTRACT_DOCUMENT_MUTATION, {
    onCompleted: () => {
      dispatchMessage(dispatch, String(i18next.t('contracts-document-upload-success')));
      setUploadingDocument(false);
      refetch();
    },
    onError: (error) => {
      dispatchException(dispatch, error);
      setUploadingDocument(false);
    },
  });

  const [deleteDocument] = useMutation(DELETE_CONTRACT_DOCUMENT_MUTATION, {
    onCompleted: () => {
      dispatchMessage(dispatch, String(i18next.t('contracts-document-delete-success')));
      refetch();
    },
    onError: (error) => {
      dispatchException(dispatch, error);
    },
  });

  const [fetchDocumentUrl] = useLazyQuery(CONTRACT_DOCUMENT_URL_QUERY);

  const handleDocumentDownload = async (documentId: number) => {
    try {
      const res = await fetchDocumentUrl({ variables: { id: documentId } });
      const url = res?.data?.contractDocumentUrl;
      if (!url) {
        throw new Error(String(i18next.t('contracts-download-url-missing')));
      }
      const href = /^https?:\/\//.test(url) ? url : getFullBackendUrl(url);
      window.open(href, '_blank');
    } catch (error: any) {
      dispatchException(dispatch, error);
    }
  };


  const handleSave = (formData: any) => {
    if (isCreate) {
      const hotelId = hotelIdFromQuery ? parseInt(hotelIdFromQuery) : NaN;
      if (!hotelId || Number.isNaN(hotelId)) {
        dispatchException(dispatch, new Error(String(i18next.t('contracts-hotel-id-missing'))));
        return;
      }
      createContract({
        variables: {
          input: {
            hotelId,
            type: formData.type,
            validFrom: formData.validFrom,
            validTo: formData.validTo,
            invoiceInterval: formData.invoiceInterval,
            amountCents: Math.round(formData.amountCents * 100),
            description: formData.description,
          },
        },
      });
    } else {
      updateContract({
        variables: {
          id: parseInt(id!),
          input: {
            type: formData.type,
            validFrom: formData.validFrom,
            validTo: formData.validTo,
            invoiceInterval: formData.invoiceInterval,
            amountCents: Math.round(formData.amountCents * 100),
            description: formData.description,
          },
        },
      });
    }
  };

  const handleDelete = async () => {
    const hotelId = contract?.hotelId;
    try {
      await deleteContract({
        variables: { id: parseInt(id!) },
        refetchQueries: hotelId
          ? [
            {
              query: LIST_HOTEL_CONTRACTS_QUERY,
              variables: { hotelId },
            },
          ]
          : [],
        awaitRefetchQueries: true,
      });
      if (hotelId) {
        navigate(`/settings/hotels/${hotelId}/contracts`);
      } else {
        navigate('/settings/hotels');
      }
    } catch (e) {
      // error handled by mutation onError
    }
  };

  const handleCreateManualInvoice = () => {
    createManualInvoice({
      variables: {
        contractId: parseInt(id!),
        invoiceDate: manualInvoiceDate,
      },
    });
  };

  const handleFileUpload = async (filename: string, mimeType: string, content: string) => {
    if (mimeType !== 'application/pdf') {
      dispatchException(dispatch, new Error(String(i18next.t('contracts-only-pdf-allowed'))));
      return;
    }

    setUploadingDocument(true);

    uploadDocument({
      variables: {
        contractId: parseInt(id!),
        filename,
        base64Data: `data:${mimeType};base64,${content}`,
      },
    });
  };

  const handleDocumentDelete = (documentId: number) => {
    if (window.confirm(String(i18next.t('contracts-document-delete-confirm')))) {
      deleteDocument({ variables: { id: documentId } });
    }
  };

  const formatAmount = (cents: number) => {
    return new Intl.NumberFormat(i18next.language, {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!isCreate && loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isCreate && !data?.contract) {
    return (
      <Box p={4}>
        <Typography>{i18next.t('contracts-not-found')}</Typography>
      </Box>
    );
  }
  const contract = data?.contract;

  const isWithinValidity = contract
    ? moment().isSameOrAfter(moment(contract.validFrom), 'day') &&
    moment().isSameOrBefore(moment(contract.validTo), 'day')
    : false;
  // Show "Aktiv" solely based on date validity, ignoring contract.isActive
  const isCurrentlyActive = !!isWithinValidity;

  const canModify = isEdit || isCreate;

  return (
    <>
      <Helmet>
        <title>{`${i18next.t('contracts-title-single')} / ${
          isCreate ? i18next.t('new') : i18next.t(contractTypeLabels[contract!.type as keyof typeof contractTypeLabels])
        }`}</title>
      </Helmet>

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(handleSave)}>
          <Box sx={{ p: 2 }}>
            <Toolbar />
            <Paper sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Header */}
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={2}>
                      <IconButton onClick={() => navigate(-1)}>
                        <ArrowBackIcon />
                      </IconButton>
                      <Typography variant="h5">
                        {i18next.t('contracts-title-single')}:{' '}
                        {isCreate ? i18next.t('new') : i18next.t(contractTypeLabels[contract!.type as keyof typeof contractTypeLabels])}
                      </Typography>
                      {!isCreate && isCurrentlyActive ? (
                        <Chip label={String(i18next.t('active'))} color="success" />
                      ) : (
                        <Chip label={String(i18next.t('inactive'))} color="default" />
                      )}
                    </Box>
                    {/* Header action buttons removed; actions available at bottom-left */}
                  </Box>
                </Grid>

                {/* Contract Details */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {i18next.t('contracts-details')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Controller
                          name="type"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              select
                              fullWidth
                              label={String(i18next.t('contracts-field-type'))}
                              {...field}
                              disabled={!canModify}
                            >
                              {Object.entries(contractTypeLabels).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                  {i18next.t(label)}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="date"
                          label={String(i18next.t('contracts-field-valid-from'))}
                          {...register('validFrom')}
                          InputLabelProps={{ shrink: true }}
                          disabled={!canModify}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="date"
                          label={String(i18next.t('contracts-field-valid-to'))}
                          {...register('validTo')}
                          InputLabelProps={{ shrink: true }}
                          disabled={!isEdit}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          name="invoiceInterval"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              select
                              fullWidth
                              label={String(i18next.t('contracts-field-interval'))}
                              {...field}
                              disabled={!canModify}
                            >
                              {Object.entries(invoiceIntervalLabels).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                  {i18next.t(label)}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label={String(i18next.t('contracts-field-amount'))}
                          {...register('amountCents')}
                          inputProps={{ step: 0.01, min: 0 }}
                          disabled={!canModify}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label={String(i18next.t('contracts-field-description'))}
                          {...register('description')}
                          disabled={!canModify}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Status Information */}
                {!isCreate && (
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        {i18next.t('contracts-status-sap')}
                      </Typography>
                      <List>
                        <ListItem>
                          <ListItemText
                            primary={String(i18next.t('contracts-header-next-invoice'))}
                            secondary={contract?.nextInvoiceDate ? moment(contract.nextInvoiceDate).format('DD.MM.YYYY') : i18next.t('contracts-no-invoice-planned')}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary={String(i18next.t('contracts-sap-status'))}
                            secondary={
                              <Chip
                                label={String(i18next.t(sapStatusLabels[contract!.sapStatus as keyof typeof sapStatusLabels]))}
                                color={contract!.sapStatus === 'SENT' ? 'success' : contract!.sapStatus === 'FAILED' ? 'error' : 'default'}
                                size="small"
                              />
                            }
                          />
                        </ListItem>
                        {contract?.sapLastSyncedAt && (
                          <ListItem>
                            <ListItemText
                              primary={String(i18next.t('contracts-sap-last-sync'))}
                              secondary={moment(contract!.sapLastSyncedAt).format('DD.MM.YYYY HH:mm')}
                            />
                          </ListItem>
                        )}
                        {contract?.sapError && (
                          <ListItem>
                            <ListItemText
                              primary={String(i18next.t('contracts-sap-error'))}
                              secondary={contract!.sapError}
                              secondaryTypographyProps={{ color: 'error' }}
                            />
                          </ListItem>
                        )}
                        <ListItem>
                          <ListItemText
                            primary={String(i18next.t('contracts-header-created-at'))}
                            secondary={moment(contract!.createdAt).format('DD.MM.YYYY HH:mm')}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText
                            primary={String(i18next.t('updated-at'))}
                            secondary={moment(contract!.updatedAt).format('DD.MM.YYYY HH:mm')}
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Grid>
                )}

                {/* Documents */}
                {!isCreate && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                          {i18next.t('documents')}
                        </Typography>
                        {isEdit && (
                          <FileUploadButton
                            name="document-upload"
                            accept="application/pdf"
                            binary={(name, type) => true}
                            onChange={handleFileUpload}
                            startIcon={<UploadIcon />}
                            disabled={uploadingDocument}
                          >
                            {uploadingDocument ? i18next.t('uploading') : i18next.t('contracts-upload-pdf')}
                          </FileUploadButton>
                        )}
                      </Box>

                      {contract?.documents && contract.documents.length > 0 ? (
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>{i18next.t('filename')}</TableCell>
                              <TableCell>{i18next.t('size')}</TableCell>
                              <TableCell>{i18next.t('uploaded-at')}</TableCell>
                              <TableCell align="center">{i18next.t('contracts-header-actions')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {contract!.documents.map((doc: any) => (
                              <TableRow key={doc.id}>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <DescriptionIcon fontSize="small" />
                                    {doc.filename}
                                  </Box>
                                </TableCell>
                                <TableCell>{formatFileSize(doc.size)}</TableCell>
                                <TableCell>{moment(doc.createdAt).format('DD.MM.YYYY HH:mm')}</TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDocumentDownload(doc.id)}
                                    title={String(i18next.t('download'))}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                  {isEdit && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDocumentDelete(doc.id)}
                                      title={String(i18next.t('delete'))}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          {i18next.t('contracts-no-documents')}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                )}

                {/* Invoice History */}
                {!isCreate && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                          {i18next.t('contracts-invoice-history')}
                        </Typography>
                        {isEdit && (
                          <Button
                            variant="outlined"
                            startIcon={<ReceiptIcon />}
                            onClick={() => setManualInvoiceDialogOpen(true)}
                          >
                            {i18next.t('contracts-create-manual-invoice')}
                          </Button>
                        )}
                      </Box>

                      {contract!.invoiceLogs && contract!.invoiceLogs.length > 0 ? (
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>{i18next.t('contracts-invoice-date')}</TableCell>
                              <TableCell>{i18next.t('contracts-triggered-at')}</TableCell>
                              <TableCell>{i18next.t('trigger')}</TableCell>
                              <TableCell>{i18next.t('contracts-sap-status')}</TableCell>
                              <TableCell>{i18next.t('error')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {contract!.invoiceLogs.map((log: any) => (
                              <TableRow key={log.id}>
                                <TableCell>{moment(log.invoiceDate).format('DD.MM.YYYY')}</TableCell>
                                <TableCell>{moment(log.triggeredAt).format('DD.MM.YYYY HH:mm')}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      log.trigger === 'AUTO'
                                        ? String(i18next.t('contracts-trigger-auto'))
                                        : log.trigger === 'MANUAL'
                                          ? String(i18next.t('contracts-trigger-manual'))
                                          : String(i18next.t('contracts-trigger-on-create'))
                                    }
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={String(i18next.t(sapStatusLabels[log.sapStatus as keyof typeof sapStatusLabels]))}
                                    color={log.sapStatus === 'SENT' ? 'success' : log.sapStatus === 'FAILED' ? 'error' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{log.error || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          {i18next.t('contracts-no-invoices')}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                )}
              </Grid>
              {canModify && (
                <Box display="flex" justifyContent="flex-start" gap={2} mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSubmit(handleSave)}
                  >
                    {isCreate ? i18next.t('create') : i18next.t('save')}
                  </Button>
                  {!isCreate && (
                    <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteDialogOpen(true)}>
                      {i18next.t('delete')}
                    </Button>
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        </form>
      </FormProvider>

      {/* Manual Invoice Dialog */}
      <Dialog open={manualInvoiceDialogOpen} onClose={() => setManualInvoiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{i18next.t('contracts-create-manual-invoice')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="date"
            label={String(i18next.t('contracts-invoice-date'))}
            value={manualInvoiceDate}
            onChange={(e) => setManualInvoiceDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualInvoiceDialogOpen(false)}>{i18next.t('cancel')}</Button>
          <Button onClick={handleCreateManualInvoice} variant="contained" color="primary">
            {i18next.t('contracts-create-invoice')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        title={String(i18next.t('contracts-delete-title'))}
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        onConfirm={handleDelete}
      >
        {i18next.t('contracts-delete-confirm')}
      </ConfirmationDialog>
    </>
  );
};

export default ContractDetail;
